import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import * as XLSX from 'xlsx';
import {
  Users,
  Building2,
  Eye,
  AlertTriangle,
  Clock,
  Search,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  Download,
  CreditCard,
} from 'lucide-react';
import { api } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

const RegionalOfficeDueDetails = () => {
  const { officeId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [loading, setLoading] = useState(true);
  const [officeData, setOfficeData] = useState(null);
  const [detailsData, setDetailsData] = useState({ drivers: [], totalItems: 0, totalPages: 0 });
  const [officeStats, setOfficeStats] = useState({
    totalDrivers: 0,
    driversWithDue: 0,
    totalDueAmount: 0,
    overdueDrivers: 0
  });

  const canView = hasPermission('financial', 'view') || hasPermission('payments', 'view');

  // Table state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [dueFilter, setDueFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('overdue');
  const [developerOnly, setDeveloperOnly] = useState('Exclude');

  // Get source information from URL params
  const urlParams = new URLSearchParams(window.location.search);
  const sourceFrom = urlParams.get('from') || 'financial';
  const sourceId = urlParams.get('sourceId');
  
  const handleBackNavigation = () => {
    console.log('RegionalOffice Back Navigation - sourceFrom:', sourceFrom, 'sourceId:', sourceId);
    
    if (sourceFrom === 'gst-details') {
      navigate('/financial/gst');
    } else if (sourceFrom === 'financial') {
      navigate('/financial/due');
    } else if (sourceFrom === 'driver-details' && sourceId) {
      navigate(`/drivers/${sourceId}`);
    } else {
      navigate('/financial/due');
    }
  };

  const fetchOfficeDetails = useCallback(async (officeId) => {
    if (!canView) return;
    try {
      setLoading(true);

      // Fetch office info only - stats will come from fetchDrivers
      const officeResponse = await api.get(`/regional-offices/${officeId}`);
      if (officeResponse.data.success) {
        setOfficeData(officeResponse.data.data);
      } else {
        setOfficeData(officeResponse.data);
      }

    } catch (error) {
      console.error('Error fetching office details:', error);
    } finally {
      setLoading(false);
    }
  }, [canView]);

  useEffect(() => {
    if (officeId && canView) {
      fetchOfficeDetails(officeId);
    }
  }, [officeId, canView, fetchOfficeDetails]);

  // Dedicated function to fetch paginated drivers using the new API endpoint
  const fetchDrivers = useCallback(async () => {
    if (!canView || !officeId || loading) return;

    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
        sortBy: sortBy,
        dueFilter: dueFilter,
        developerOnly: developerOnly
      };

      console.log('🔍 Fetching drivers with filters:', params);

      const response = await api.get(`/payments/due/office/${officeId}/drivers`, {
        params
      });

      console.log('📨 API Response:', response.data);

      if (response.data.success) {
        const { drivers, pagination, summary, filters } = response.data.data;

        console.log('🚗 Fetched drivers via dedicated API:', {
          filterApplied: filters.dueFilter,
          totalDrivers: summary.totalDrivers,
          driversWithDue: summary.driversWithDue,
          overdueDrivers: summary.overdueDrivers,
          totalDueAmount: summary.totalDueAmount,
          currentPage: pagination.current,
          totalPages: pagination.total,
          driversReturned: drivers.length
        });

        // Update drivers data
        setDetailsData({
          drivers: drivers,
          totalItems: pagination.totalRecords,
          totalPages: pagination.total
        });

        // Update stats data from the API response
        setOfficeStats({
          totalDrivers: summary.totalDrivers,
          driversWithDue: summary.driversWithDue,
          totalDueAmount: summary.totalDueAmount,
          overdueDrivers: summary.overdueDrivers
        });
      } else {
        console.error('❌ API returned success: false', response.data);
      }
    } catch (error) {
      console.error('Error fetching drivers via dedicated API:', error);
    }
  }, [officeId, currentPage, itemsPerPage, searchTerm, sortBy, dueFilter, developerOnly, loading]);

  // Effect for initial driver load
  useEffect(() => {
    fetchDrivers();
  }, [fetchDrivers]);

  const getDueStatus = (nextDueDate) => {
    if (!nextDueDate) {
      return { status: 'no-date', text: 'No due date', color: 'text-gray-600 bg-gray-100' };
    }

    // Get current date in IST (UTC+5:30)
    const nowIST = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
    const todayIST = new Date(nowIST.getFullYear(), nowIST.getMonth(), nowIST.getDate());

    // Get due date in IST
    const dueDateIST = new Date(new Date(nextDueDate).toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
    const dueDateOnlyIST = new Date(dueDateIST.getFullYear(), dueDateIST.getMonth(), dueDateIST.getDate());

    // Calculate difference in days
    const diffTime = dueDateOnlyIST.getTime() - todayIST.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return { status: 'overdue', text: `Overdue (${Math.abs(diffDays)} days)`, color: 'text-red-600 bg-red-100' };
    } else if (diffDays === 0) {
      return { status: 'today', text: 'Due Today', color: 'text-orange-600 bg-orange-100' };
    } else {
      return { status: 'pending', text: `Due in ${diffDays} days`, color: 'text-blue-600 bg-blue-100' };
    }
  };



  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'Asia/Kolkata'
    });
  };

  const handleDriverClick = (driverId) => {
    const currentParams = new URLSearchParams({
      from: 'due-details',
      sourceId: officeId,
      originalSource: sourceFrom || 'financial'
    });
    navigate(`/drivers/${driverId}?${currentParams.toString()}`);
  };

  // Export functionality

  // Export functionality
  const handleExportToExcel = async () => {
    try {
      toast.loading('Preparing export...', { id: 'export' });
      
      // Get all filtered data for export (remove pagination limit but keep filters)
      const exportParams = {
        page: 1,
        limit: 10000, // Get all filtered records
        search: searchTerm,
        sortBy: sortBy,
        dueFilter: dueFilter,
        developerOnly: developerOnly
      };
      
      const exportResponse = await api.get(`/payments/due/office/${officeId}/drivers`, {
        params: exportParams
      });
      
      if (!exportResponse?.data?.success) {
        toast.error('Failed to fetch data for export', { id: 'export' });
        return;
      }
      
      const exportData = exportResponse.data.data.drivers || [];
      
      if (exportData.length === 0) {
        toast.error('No data available for export with current filters', { id: 'export' });
        return;
      }
      
      toast.loading('Generating Excel file...', { id: 'export' });
      
      // Prepare data for Excel export
      const excelData = exportData.map((driver, index) => {
        const dueStatus = getDueStatus(driver.nextDueDate);
        
        return {
          'S.No': index + 1,
          'Driver Name': driver.name || 'N/A',
          'Phone': driver.phone || 'N/A',
          'Driver ID': driver._id?.toString().slice(-8) || 'N/A',
          'Developer': driver.dev ? 'Yes' : 'No',
          'Total Due (₹)': (driver.driverDue || 0).toFixed(2),
          'Total Earnings (₹)': (driver.driverEarnings || 0).toFixed(2),
          'Next Due Date': driver.nextDueDate ? formatDate(driver.nextDueDate) : 'No due date',
          'Due Status': dueStatus.text,
          'Payment Status': driver.paymentStatus || 'N/A',
          'Is Approved': driver.isApproved ? 'Yes' : 'No',
          'Is Blocked': driver.isBlocked ? 'Yes' : 'No',
          'Created On': driver.createdOn ? formatDate(driver.createdOn) : 'N/A'
        };
      });
      
      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      
      // Set column widths
      const colWidths = [
        { wch: 8 },  // S.No
        { wch: 20 }, // Driver Name
        { wch: 15 }, // Phone
        { wch: 15 }, // Driver ID
        { wch: 12 }, // Developer
        { wch: 15 }, // Total Due
        { wch: 18 }, // Total Earnings
        { wch: 15 }, // Next Due Date
        { wch: 20 }, // Due Status
        { wch: 15 }, // Payment Status
        { wch: 12 }, // Is Approved
        { wch: 12 }, // Is Blocked
      ];
      worksheet['!cols'] = colWidths;
      
      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Drivers Due Report');
      
      // Generate filename with current date and filter info
      const currentDate = new Date().toISOString().split('T')[0];
      const officeName = officeData?.name?.replace(/[^a-zA-Z0-9]/g, '_') || 'Office';
      let filename = `Drivers_Due_Report_${officeName}_${currentDate}`;
      
      // Add filter info to filename if filters are applied
      const hasFilters = dueFilter !== 'all' || searchTerm || developerOnly !== 'Exclude' || sortBy !== 'overdue';
      if (hasFilters) {
        filename += '_filtered';
      }
      filename += '.xlsx';
      
      // Download the file
      XLSX.writeFile(workbook, filename);
      
      toast.success(`Exported ${excelData.length} filtered records successfully!`, { id: 'export' });
      
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export data. Please try again.', { id: 'export' });
    }
  };


  if (!canView) {
    return (
      <div className="empty-state">
        <CreditCard className="empty-state-icon" />
        <h3 className="empty-state-title">Access Denied</h3>
        <p className="empty-state-description">
          You don't have permission to view regional office due details.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={handleBackNavigation}
          className="p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">
            Payment Details - {officeData?.name || 'Regional Office'}
          </h1>
          <p className="text-gray-600">{officeData?.address}</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate(`/financial/office/${officeId}/trips`, {
              state: { source: 'financial', officeName: officeData?.name }
            })}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center"
          >
            <Eye className="h-4 w-4 mr-2" />
            View All Trips
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 rounded-md bg-blue-100">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">
                All Drivers
              </h3>
              <p className="text-2xl font-bold text-blue-600">{officeStats.totalDrivers}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 rounded-md bg-green-100">
              <Building2 className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">Drivers with Due</h3>
              <p className="text-2xl font-bold text-green-600">
                {officeStats.driversWithDue}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 rounded-md bg-red-100">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">Total Due Amount</h3>
              <p className="text-2xl font-bold text-red-600">
                {formatCurrency(officeStats.totalDueAmount)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 rounded-md bg-yellow-100">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">Overdue Drivers</h3>
              <p className="text-2xl font-bold text-yellow-600">
                {officeStats.overdueDrivers}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Page Title */}
      <div className="border-b border-gray-200 pb-4">
        <div className="flex items-center space-x-2">
          <Users className="h-6 w-6 text-indigo-600" />
          <h2 className="text-xl font-semibold text-gray-900">
            All Drivers in {officeData?.name} ({officeStats.totalDrivers} total)
          </h2>
        </div>
        <p className="text-sm text-gray-600 mt-2">
          Showing all drivers registered under this regional office, including their due payment status
        </p>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow p-6">
        {/* Filter indicator */}
        <div className="mb-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-xl font-medium ">Filters</span>
            </div>
            <div className="md:col-span-4 flex justify-end space-x-2 mt-2">
              <button
                onClick={() => {
                  setDueFilter('all');
                  setSearchTerm('');
                  setDeveloperOnly('Exclude');
                  setSortBy('overdue');
                  setCurrentPage(1);
                }}
                className="btn btn-outline btn-sm"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 items-end">
          {/* Search Field - Takes full width on mobile, spans 2 columns on large screens */}
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or phone..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className={`pl-10 pr-4 py-2 w-full border rounded-md focus:ring-indigo-500 focus:border-indigo-500 transition-colors ${searchTerm ? 'border-blue-500 bg-blue-50 shadow-md ring-1 ring-blue-200' : 'border-gray-300'
                  }`}
              />
            </div>
          </div>

          {/* Filter Dropdowns */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Drivers</label>
            <select
              value={dueFilter}
              onChange={(e) => {
                setDueFilter(e.target.value);
                setCurrentPage(1);
              }}
              className={`w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500 transition-colors ${dueFilter !== 'all' ? 'border-blue-500 bg-blue-50 shadow-md ring-1 ring-blue-200' : 'border-gray-300'
                }`}
            >
              <option value="all">All Drivers</option>
              <option value="due">Drivers with Due</option>
              <option value="overdue">Overdue Only</option>
              <option value="today">Due Today</option>
              <option value="pending">Due Later</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sort by</label>
            <select
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value);
                setCurrentPage(1);
              }}
              className={`w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500 transition-colors ${sortBy !== 'overdue' ? 'border-blue-500 bg-blue-50 shadow-md ring-1 ring-blue-200' : 'border-gray-300'
                }`}
            >
              <option value="overdue">Sort by Due Date</option>
              <option value="name">Sort by Name</option>
              <option value="amount">Sort by Due Amount</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Developer Filter</label>
            <select
              value={developerOnly}
              onChange={(e) => {
                setDeveloperOnly(e.target.value);
                setCurrentPage(1);
              }}
              className={`w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500 transition-colors ${developerOnly !== 'Exclude' ? 'border-blue-500 bg-blue-50 shadow-md ring-1 ring-blue-200' : 'border-gray-300'
                }`}
            >
              <option value="Exclude">Exclude Developers</option>
              <option value="Include">Include Developers</option>
              <option value="Only">Developers Only</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">
              Due Report ({detailsData.totalItems} records)
            </h3>
            <button
              onClick={handleExportToExcel}
              disabled={!detailsData.drivers?.length}
              className="btn btn-outline"
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </button>
          </div>
        </div>
        <div className="overflow-x-auto card-body">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Driver
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Due
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Earnings
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Due Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {detailsData.drivers?.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center">
                      <Users className="h-12 w-12 text-gray-400 mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        {dueFilter === 'all' ? 'No drivers found' :
                          dueFilter === 'due' ? 'No drivers with due amounts' :
                            dueFilter === 'overdue' ? 'No overdue drivers' :
                              dueFilter === 'today' ? 'No drivers due today' :
                                dueFilter === 'pending' ? 'No drivers with future due dates' :
                                  'No drivers found'}
                      </h3>
                      <p className="text-gray-500">
                        {dueFilter === 'all'
                          ? 'There are no drivers registered in this regional office.'
                          : searchTerm
                            ? `No drivers match your search "${searchTerm}" with the selected filter.`
                            : 'Try changing the filter or search criteria to find drivers.'}
                      </p>
                      {(dueFilter !== 'all' || searchTerm || developerOnly !== 'Exclude') && (
                        <button
                          onClick={() => {
                            setDueFilter('all');
                            setSearchTerm('');
                            setDeveloperOnly('Exclude');
                            setSortBy('overdue');
                            setCurrentPage(1);
                          }}
                          className="mt-3 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                        >
                          Clear Filters
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                detailsData.drivers?.map((driver) => (
                  <tr key={driver._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full flex items-center justify-center bg-blue-100">
                          <Users className="h-5 w-5 text-blue-600" />
                        </div>
                        <div className="ml-4">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleDriverClick(driver._id)}
                              className="text-sm font-medium text-indigo-600 hover:text-indigo-900 cursor-pointer"
                            >
                              {driver.name}
                            </button>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            ID: {driver._id.toString().slice(-8)}
                          </div>
                          {driver.dueCycle && (
                            <div className="text-xs text-gray-500">
                              Due Cycle: {driver.dueCycle.value} {driver.dueCycle.type}(s)
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm font-medium ${(driver.driverDue || 0) > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                        {formatCurrency(driver.driverDue || 0)}
                      </div>
                      {(driver.driverDue || 0) === 0 && (
                        <div className="text-xs text-green-600 mt-1">
                          No dues
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatCurrency(driver.driverEarnings || 0)}
                      </div>
                      {driver.nextDueDate && (
                        <div className="text-xs text-gray-500 mt-1">
                          Next due: {formatDate(driver.nextDueDate)}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {driver.nextDueDate ? (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getDueStatus(driver.nextDueDate).color}`}>
                          {getDueStatus(driver.nextDueDate).text}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          No due date
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex flex-col space-y-1">
                        <button
                          onClick={() => handleDriverClick(driver._id)}
                          className="text-indigo-600 hover:text-indigo-900 flex items-center text-xs"
                        >
                          <Users className="h-3 w-3 mr-1" />
                          View Profile
                        </button>
                        {/* {(driver.driverDue || 0) > 0 && (
                        <button
                          onClick={() => {
                            navigate(`/financial/due/driver/${driver._id}/trips`, {
                              state: { 
                                source: 'financial',
                                officeId: officeId,
                                officeName: officeData?.name
                              }
                            });
                          }}
                          className="text-green-600 hover:text-green-900 flex items-center text-xs"
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          Due Trips
                        </button>
                      )} */}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {detailsData.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, detailsData.totalItems)} of {detailsData.totalItems} results
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="p-2 rounded-md border border-gray-300 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>

                {Array.from({ length: detailsData.totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-2 text-sm rounded-md ${currentPage === page
                      ? 'bg-indigo-600 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                      }`}
                  >
                    {page}
                  </button>
                ))}

                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, detailsData.totalPages))}
                  disabled={currentPage === detailsData.totalPages}
                  className="p-2 rounded-md border border-gray-300 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default RegionalOfficeDueDetails;
